import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { TrendingUp, MapPin, Building2, DollarSign } from "lucide-react";
import { isCrawlableTrendsLink, isCrawlableBestStatesLink } from "@/lib/seoIndexing";
import { hubsForOccupation } from "@/lib/highestPayingHubs";

interface OccupationHubLinksProps {
  occupationSlug: string;
  location?: string;
  socCode?: string;
}

/**
 * Hub links for an occupation - trends, best states, industry, and relevant
 * highest-paying comparison hubs (bidirectional internal links for G3).
 */
export default function OccupationHubLinks({
  occupationSlug,
  location = 'united-states',
  socCode,
}: OccupationHubLinksProps) {
  const occupationDisplay = occupationSlug
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

  const trendsCrawlable = isCrawlableTrendsLink(occupationSlug, location);
  const bestStatesCrawlable = isCrawlableBestStatesLink(occupationSlug);
  const relatedHubs = hubsForOccupation(socCode).slice(0, 4);

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>Explore More Data for {occupationDisplay}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link
            to={`/trends/${occupationSlug}/${location}/`}
            {...(!trendsCrawlable && { rel: 'nofollow' })}
            className="flex flex-col items-center gap-2 p-4 rounded-lg border hover:bg-muted transition-colors text-center"
          >
            <TrendingUp className="h-6 w-6 text-primary" />
            <span className="font-medium">Salary Trends</span>
            <span className="text-sm text-muted-foreground">
              Historical wage data
            </span>
          </Link>
          
          <Link
            to={`/best-states/${occupationSlug}/`}
            {...(!bestStatesCrawlable && { rel: 'nofollow' })}
            className="flex flex-col items-center gap-2 p-4 rounded-lg border hover:bg-muted transition-colors text-center"
          >
            <MapPin className="h-6 w-6 text-primary" />
            <span className="font-medium">Best States</span>
            <span className="text-sm text-muted-foreground">
              Compare by location
            </span>
          </Link>
          
          <Link
            to={`/industry/`}
            className="flex flex-col items-center gap-2 p-4 rounded-lg border hover:bg-muted transition-colors text-center"
          >
            <Building2 className="h-6 w-6 text-primary" />
            <span className="font-medium">Industries</span>
            <span className="text-sm text-muted-foreground">
              Find by industry
            </span>
          </Link>
        </div>

        {relatedHubs.length > 0 && (
          <div className="border-t pt-4">
            <div className="flex items-center gap-2 mb-3 text-sm font-medium">
              <DollarSign className="h-4 w-4 text-primary" />
              Related pay rankings
            </div>
            <div className="flex flex-wrap gap-2">
              {relatedHubs.map((hub) => (
                <Link
                  key={hub.slug}
                  to={hub.existingRoute || `/highest-paying/${hub.slug}/`}
                  className="text-sm px-3 py-1.5 rounded-md border hover:bg-muted text-primary"
                >
                  {hub.title}
                </Link>
              ))}
              <Link
                to="/occupations/"
                className="text-sm px-3 py-1.5 rounded-md border hover:bg-muted text-muted-foreground"
              >
                All occupations
              </Link>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
